import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { OnSiteAppComponentPropsType } from '@purecarslabs/signal-core/baseline/on-site-app/types'
/* webpack-strip-block:removed */
import renderIf from '@purecarslabs/signal-utils/renderIf'
import EmbedTrigger from '@app-src/components/EmbedTrigger'
import ModalTrigger from '@app-src/components/ModalTrigger'
import { NativeLeadType } from '@app-src/data/leads/types.ts'
import { FORM_INJECT_ANCHOR_CLASSNAME } from '@app-src/constants'
import subscribeToPriceDrop from '@app-src/data/price-drops/subscribeToPriceDrop'
import { cookLeadForPriceDrops } from '@app-src/data/price-drops/utils'
import submitLeadCommunication from '@app-src/data/value-report/submitLeadCommunication'
import { debugLogForms } from '@app-src/debug'
import { FormDetailsType, MvhColorsType } from '@app-src/types'
import {
  EXTRA_SEND_TO_PHONE,
  EXTRA_SUBSCRIBE_TO_PRICE_DROPS
} from './constants'
import { useShowFormApi } from './hooks'
import { injectCustomFonts } from './styles'

type ApiContainerPropsType = {
  initialFormDetails?: FormDetailsType
  initialMvhColors?: MvhColorsType
  testEmbed?: boolean
  onAfterSubmitLeadTest?: (
    lead: NativeLeadType,
    forModal: boolean,
    extraFlags: number
  ) => void
} & OnSiteAppComponentPropsType

// this is the form trigger component, which handles owning the modal which is generated containing
// the form
export const ApiContainer: React.FC<ApiContainerPropsType> = ({
  initialFormDetails,
  initialMvhColors,
  injectRoot,
  stylesInPage,
  /* webpack-strip-block:removed */
  ...rest
}) => {
  // hooks, state, and refs
  /////////////////////////

  const [activeEmbedFormDetails, setActiveEmbedFormDetails] = useState<
    FormDetailsType | undefined
  >(initialFormDetails)
  const [activeModalFormDetails, setActiveModalFormDetails] = useState<
    FormDetailsType | undefined
  >(initialFormDetails)
  const [mvhColors, setMvhColors] = useState<MvhColorsType | undefined | null>(
    initialMvhColors
  )
  const [formLoaded, setFormLoaded] = useState(false)
  const [embedExtraFlags, setEmbedExtraFlags] = useState(0)
  const [modalExtraFlags, setModalExtraFlags] = useState(0)
  const submittedModalLeadRef = useRef<NativeLeadType | undefined>()
  const hasEmbed =
    /* webpack-strip-block:removed */
    injectRoot?.className?.includes(FORM_INJECT_ANCHOR_CLASSNAME)

  // set up the form API
  useShowFormApi(
    activeModalFormDetails,
    setActiveEmbedFormDetails,
    setActiveModalFormDetails,
    setModalExtraFlags,
    setMvhColors,
    submittedModalLeadRef
  )

  // callbacks
  ////////////

  const handleAfterSubmitLead = useCallback(
    (forModal: boolean) => async (lead: NativeLeadType) => {
      const extraFlags = forModal ? modalExtraFlags : embedExtraFlags
      if (forModal && submittedModalLeadRef) {
        submittedModalLeadRef.current = lead
      }

      debugLogForms('after form submission', forModal, extraFlags)

      if (
        // eslint-disable-next-line prettier/prettier
        extraFlags & EXTRA_SUBSCRIBE_TO_PRICE_DROPS
        /* webpack-strip-block:removed */
      ) {
        try {
          debugLogForms('submitting price drop alert')
          await subscribeToPriceDrop({ payload: cookLeadForPriceDrops(lead) })
        } catch (err) {
          debugLogForms('saw price drop submit error', err)
        }
      }

      if (
        // eslint-disable-next-line prettier/prettier
        extraFlags & EXTRA_SEND_TO_PHONE
        /* webpack-strip-block:removed */
      ) {
        try {
          debugLogForms('submitting send to phone')
          await submitLeadCommunication({
            // https://docs.google.com/spreadsheets/d/1IESZ3e0NdV8UggZqQQQBAHfXpV41xpvNtNK5IpU895o/edit?gid=0#gid=0
            // whenever we request send to mobile, we use callToActionEnum 8
            callToActionType: 8,
            lead
          })
        } catch (err) {
          debugLogForms('saw sent to phone error', err)
        }
      }

      /* webpack-strip-block:removed */
    },
    [
      embedExtraFlags,
      modalExtraFlags,
      /* webpack-strip-block:removed */
    ]
  )

  // effects
  //////////

  useEffect(() => {
    if (stylesInPage) {
      // inject the custom HTML outside the shadow DOM using the provided "stylesInPage"
      stylesInPage.innerHTML = injectCustomFonts(mvhColors)
    }
  }, [mvhColors, stylesInPage])

  return (
    <>
      {renderIf(Boolean(activeEmbedFormDetails) && hasEmbed, () => (
        <EmbedTrigger
          activeFormDetails={activeEmbedFormDetails!}
          formLoaded={formLoaded}
          extraFlags={embedExtraFlags}
          mvhColors={mvhColors}
          onAfterSubmitLead={handleAfterSubmitLead(false /*forModal*/)}
          requirePhone={false}
          setActiveFormDetails={setActiveEmbedFormDetails}
          setExtraFlags={setEmbedExtraFlags}
          setFormLoaded={setFormLoaded}
          setMvhColors={setMvhColors}
          {...rest}
        />
      ))}
      {renderIf(Boolean(activeModalFormDetails), () => (
        <ModalTrigger
          activeFormDetails={activeModalFormDetails!}
          formLoaded={formLoaded}
          extraFlags={modalExtraFlags}
          mvhColors={mvhColors}
          onAfterSubmitLead={handleAfterSubmitLead(true /*forModal*/)}
          requirePhone={Boolean(modalExtraFlags & EXTRA_SEND_TO_PHONE)}
          setActiveFormDetails={setActiveModalFormDetails}
          setExtraFlags={setModalExtraFlags}
          setFormLoaded={setFormLoaded}
          setMvhColors={setMvhColors}
          {...rest}
        />
      ))}
    </>
  )
}

export default memo(ApiContainer)
